import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Activity, AlertTriangle, CheckCircle2, Play } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatEastern } from "@/lib/utils";

type Sample = {
  id: string;
  captured_at: string;
  label: string;
  calls: number;
  delta_calls: number;
  mean_ms: number;
  max_ms: number;
  baseline_ms: number | null;
  spiked: boolean;
};

const WINDOWS = [
  { label: "24h", hours: 24 },
  { label: "7d", hours: 24 * 7 },
  { label: "30d", hours: 24 * 30 },
];

const Spark = ({ values }: { values: number[] }) => {
  if (values.length < 2) return <span className="text-muted-foreground">—</span>;
  const max = Math.max(...values, 1);
  return (
    <span className="inline-flex items-end gap-[2px] h-6">
      {values.slice(-32).map((v, i) => (
        <span
          key={i}
          className="w-[3px] bg-primary/70"
          style={{ height: `${Math.max(2, (v / max) * 24)}px` }}
          title={`${v.toFixed(1)}ms`}
        />
      ))}
    </span>
  );
};

export default function AdminPerformance() {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [hours, setHours] = useState(24);

  const load = async () => {
    setLoading(true);
    const since = new Date(Date.now() - hours * 3600_000).toISOString();
    const { data, error } = await supabase
      .from("query_perf_samples")
      .select("id,captured_at,label,calls,delta_calls,mean_ms,max_ms,baseline_ms,spiked")
      .gte("captured_at", since)
      .order("captured_at", { ascending: false })
      .limit(2000);
    if (error) toast.error(error.message);
    setSamples((data ?? []) as Sample[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, [hours]);

  const runNow = async () => {
    setRunning(true);
    const { data, error } = await supabase.functions.invoke("db-perf-monitor");
    setRunning(false);
    if (error) return toast.error(error.message);
    const spikes = (data as any)?.spikes?.length ?? 0;
    toast.success(spikes ? `Sampled — ${spikes} slow query alert(s) sent` : "Sampled — all queries within thresholds");
    load();
  };

  const grouped = useMemo(() => {
    const map = new Map<string, Sample[]>();
    for (const s of samples) {
      const arr = map.get(s.label) ?? [];
      arr.push(s);
      map.set(s.label, arr);
    }
    return [...map.entries()]
      .map(([label, rows]) => {
        const latest = rows[0];
        const series = [...rows].reverse().map((r) => Number(r.mean_ms));
        const worst = Math.max(...rows.map((r) => Number(r.max_ms)));
        const calls = rows.reduce((a, r) => a + Number(r.delta_calls || 0), 0);
        return { label, latest, series, worst, calls, spikes: rows.filter((r) => r.spiked).length };
      })
      .sort((a, b) => Number(b.latest.mean_ms) - Number(a.latest.mean_ms));
  }, [samples]);

  const alerting = grouped.filter((g) => g.latest.spiked);

  return (
    <AdminShell title="PERFORMANCE" actions={
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={runNow} disabled={running}>
          {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />} Sample now
        </Button>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Refresh
        </Button>
      </div>
    }>
      <div className="flex items-center gap-2 mb-6">
        {WINDOWS.map((w) => (
          <Button key={w.label} size="sm" variant={hours === w.hours ? "default" : "outline"} onClick={() => setHours(w.hours)}>
            {w.label}
          </Button>
        ))}
        <span className="ml-auto flex items-center gap-2 font-mono text-xs text-muted-foreground">
          <Activity className="w-3 h-3" /> sampled every 15 min · alerts on 3× baseline or &gt;1s
        </span>
      </div>

      <div className={`border p-4 mb-6 flex items-start gap-3 ${alerting.length ? "border-red-500/40 bg-red-500/10" : "border-emerald-500/40 bg-emerald-500/10"}`}>
        {alerting.length ? <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" /> : <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />}
        <div className="text-sm font-body">
          <p className="font-display tracking-widest text-xs mb-1">
            {alerting.length ? `${alerting.length} QUERY GROUP(S) SLOW` : "ALL BOOKING QUERIES HEALTHY"}
          </p>
          <p className="text-muted-foreground text-xs">
            {alerting.length
              ? alerting.map((a) => a.label).join(", ")
              : "Availability lookups, slot-lock logic and webhook writes are inside their normal latency band."}
          </p>
        </div>
      </div>

      {loading && !samples.length ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : grouped.length === 0 ? (
        <p className="text-sm text-muted-foreground font-body">
          No samples in this window yet. Hit “Sample now” to capture the first datapoint.
        </p>
      ) : (
        <div className="bg-panel border border-border overflow-x-auto">
          <table className="w-full text-sm font-body min-w-[820px]">
            <thead>
              <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="p-3">Query group</th>
                <th className="p-3">Trend (avg ms)</th>
                <th className="p-3 text-right">Latest avg</th>
                <th className="p-3 text-right">Baseline</th>
                <th className="p-3 text-right">Worst</th>
                <th className="p-3 text-right">Calls</th>
                <th className="p-3 text-right">Last sample</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => (
                <tr key={g.label} className={`border-b border-border last:border-0 ${g.latest.spiked ? "bg-red-500/5" : ""}`}>
                  <td className="p-3 font-mono text-xs">
                    {g.label}
                    {g.spikes > 0 && <span className="ml-2 text-red-400">· {g.spikes} spike(s)</span>}
                  </td>
                  <td className="p-3"><Spark values={g.series} /></td>
                  <td className={`p-3 text-right font-mono ${g.latest.spiked ? "text-red-400" : ""}`}>{Number(g.latest.mean_ms).toFixed(2)}ms</td>
                  <td className="p-3 text-right font-mono text-muted-foreground">
                    {g.latest.baseline_ms !== null ? `${Number(g.latest.baseline_ms).toFixed(2)}ms` : "—"}
                  </td>
                  <td className="p-3 text-right font-mono text-muted-foreground">{g.worst.toFixed(0)}ms</td>
                  <td className="p-3 text-right font-mono text-muted-foreground">{g.calls.toLocaleString()}</td>
                  <td className="p-3 text-right text-xs text-muted-foreground">{formatEastern(g.latest.captured_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs font-mono text-muted-foreground mt-4">
        Samples come from Postgres statement statistics for availability lookups, slot-lock RPCs and webhook writes.
        A group alerts (email + in-app) when its interval average exceeds 3× its 7-day median or 1 second.
      </p>
    </AdminShell>
  );
}
